import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatBRL, formatBRLFull } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Brain, Trophy, Users, TrendingUp, Ban, Building2, Award, CalendarClock, Target, ArrowUpRight, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function StatBig({ icon: Ico, label, value, sub, color = "blue", testid }) {
  return (
    <Card className="p-6 rounded-2xl" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</div>
        <div className={`w-9 h-9 rounded-lg bg-${color}-50 text-${color}-600 flex items-center justify-center`}>
          <Ico className="w-5 h-5" />
        </div>
      </div>
      <div className="font-display text-3xl md:text-4xl font-bold tabular-nums mt-3 text-slate-900">
        {value}
      </div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </Card>
  );
}

export default function InteligenciaComercial() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [metaDiaria, setMetaDiaria] = useState(null);
  const [avisos, setAvisos] = useState(null);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(""); // "" = ano inteiro
  const [vendedoraId, setVendedoraId] = useState("");
  const [vendedoras, setVendedoras] = useState([]);

  useEffect(() => {
    if (user?.role === "diretoria" || user?.role === "supervisao") {
      api.get("/users").then((r) => setVendedoras((r.data || []).filter((u) => u.role === "vendedora"))).catch(() => {});
    }
  }, [user?.role]);

  const load = () => {
    const params = new URLSearchParams({ ano: String(ano) });
    if (mes) params.set("mes", mes);
    if (vendedoraId) params.set("vendedora_id", vendedoraId);
    api.get(`/inteligencia?${params.toString()}`).then((r) => setData(r.data));
    if (user?.role === "diretoria" || user?.role === "supervisao") {
      api.get("/dashboards/meta-diaria").then((r) => setMetaDiaria(r.data)).catch(() => {});
    }
    // Avisos de carteira (topo)
    const p2 = new URLSearchParams();
    if (vendedoraId) p2.set("vendedora_id", vendedoraId);
    api.get(`/inteligencia/avisos-carteira${p2.toString() ? `?${p2}` : ""}`)
      .then((r) => setAvisos(r.data)).catch(() => setAvisos(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ano, mes, vendedoraId]);

  const removerBlacklist = async (mun) => {
    if (!window.confirm(`Remover ${mun.nome} da blacklist?`)) return;
    try {
      await api.patch(`/municipios/${mun.id}/blacklist`, { blacklist: false, motivo: "" });
      toast.success("Removido da blacklist");
      load();
    } catch { toast.error("Erro"); }
  };

  if (!data) return <div className="text-slate-500">Carregando inteligência...</div>;

  const periodoLabel = mes ? `${String(mes).padStart(2, "0")}/${data.ano}` : `${data.ano}`;

  return (
    <div className="space-y-6" data-testid="inteligencia-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Brain className="w-6 h-6 text-indigo-600" />
            <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Inteligência Comercial</h1>
          </div>
          <p className="text-slate-500 text-sm">Indicadores · {periodoLabel}{vendedoraId ? ` · vendedora selecionada` : ""}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(user?.role === "diretoria" || user?.role === "supervisao") && (
            <select
              value={vendedoraId}
              onChange={(e) => setVendedoraId(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
              data-testid="filter-vendedora"
            >
              <option value="">Todas as vendedoras</option>
              {vendedoras.map((v) => (<option key={v.id} value={v.id}>{v.nome}</option>))}
            </select>
          )}
          <select
            value={mes}
            onChange={(e) => setMes(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
            data-testid="filter-mes"
          >
            <option value="">Ano inteiro</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
            ))}
          </select>
          <select
            value={ano}
            onChange={(e) => setAno(Number(e.target.value))}
            className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
            data-testid="filter-ano"
          >
            {[0, 1, 2].map((i) => {
              const y = new Date().getFullYear() - i;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
        </div>
      </div>

      {/* AVISOS DE CARTEIRA — topo (3 blocos com sub-colunas) */}
      {avisos && (
        <Card className="p-6 rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50/50 to-white" data-testid="avisos-carteira">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h2 className="font-display font-bold text-xl text-slate-900">Avisos de Carteira</h2>
          </div>

          {/* BLOCO 1 — Mudanças de categoria (3 sub-colunas) */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <ArrowUpRight className="w-4 h-4 text-blue-600" />
              <h3 className="font-bold text-sm text-blue-900 uppercase tracking-wider">Mudanças de categoria de carteira</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              {[
                { tipo: "nao_cliente_para_consumidor", label: "Não Cliente → Consumidor", cor: "blue", catAlvo: "consumidor" },
                { tipo: "blacklist_para_consumidor", label: "Blacklist → Consumidor", cor: "purple", catAlvo: "consumidor" },
                { tipo: "consumidor_para_cliente", label: "Consumidor → Cliente", cor: "emerald", catAlvo: "cliente" },
              ].map((sub) => {
                const items = avisos.mudancas_categoria.filter((m) => m.tipo === sub.tipo);
                return (
                  <div key={sub.tipo} className={`rounded-xl border border-${sub.cor}-200 bg-${sub.cor}-50/50 p-3`} data-testid={`aviso-mud-${sub.tipo}`}>
                    <div className={`text-[11px] font-bold text-${sub.cor}-900 uppercase tracking-wider mb-2 flex items-center justify-between`}>
                      <span className="truncate">{sub.label}</span>
                      <span className={`px-1.5 py-0.5 rounded-full bg-${sub.cor}-500 text-white text-[10px] tabular-nums`}>{items.length}</span>
                    </div>
                    {items.length === 0 && <div className="text-xs text-slate-400 italic">Nenhum registro.</div>}
                    <div className="space-y-1 max-h-48 overflow-auto">
                      {items.map((mu, i) => (
                        <button
                          key={i}
                          onClick={() => nav(`/municipio/${mu.municipio_id}?cat=${sub.catAlvo}`)}
                          className={`w-full text-left rounded-lg bg-white border border-${sub.cor}-100 hover:border-${sub.cor}-400 hover:shadow-sm transition p-2`}
                          data-testid={`aviso-mud-${sub.tipo}-${i}`}
                        >
                          <div className="font-semibold text-sm text-slate-800 flex items-center justify-between gap-2">
                            <span className="truncate">{mu.municipio_nome} · {mu.uf}</span>
                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full bg-${sub.cor}-500 text-white tabular-nums flex-shrink-0`}>{mu.qtd}</span>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5"><b>{mu.mes_ano}</b></div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* BLOCO 2 — Ausências de contato > 30 dias (2 sub-colunas) */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-amber-600" />
              <h3 className="font-bold text-sm text-amber-900 uppercase tracking-wider">Ausências de contato (mais de 30 dias)</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                { cat: "consumidor", label: "Consumidor sem contato", cor: "amber" },
                { cat: "cliente", label: "Cliente sem prospecção", cor: "emerald" },
              ].map((sub) => {
                const items = avisos.ausencia_contato.filter((a) => a.categoria === sub.cat);
                return (
                  <div key={sub.cat} className={`rounded-xl border border-${sub.cor}-200 bg-${sub.cor}-50/50 p-3`} data-testid={`aviso-contato-${sub.cat}`}>
                    <div className={`text-[11px] font-bold text-${sub.cor}-900 uppercase tracking-wider mb-2 flex items-center justify-between`}>
                      <span className="truncate">{sub.label}</span>
                      <span className={`px-1.5 py-0.5 rounded-full bg-${sub.cor}-500 text-white text-[10px] tabular-nums`}>{items.length}</span>
                    </div>
                    {items.length === 0 && <div className="text-xs text-slate-400 italic">Todos com contato recente.</div>}
                    <div className="space-y-1 max-h-48 overflow-auto">
                      {items.map((a, i) => (
                        <button
                          key={i}
                          onClick={() => nav(`/municipio/${a.municipio_id}?cat=${a.categoria}`)}
                          className={`w-full text-left rounded-lg bg-white border border-${sub.cor}-100 hover:border-${sub.cor}-400 hover:shadow-sm transition p-2`}
                          data-testid={`aviso-contato-${sub.cat}-${i}`}
                        >
                          <div className="font-semibold text-sm text-slate-800 flex items-center justify-between gap-2">
                            <span className="truncate">{a.municipio_nome} · {a.uf}</span>
                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full bg-${sub.cor}-500 text-white tabular-nums flex-shrink-0`}>{a.qtd}</span>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5"><b>{a.max_dias} dia{a.max_dias !== 1 ? "s" : ""}</b> sem contato</div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* BLOCO 3 — Ausências de frequência > 90 dias (2 sub-colunas) */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-red-600" />
              <h3 className="font-bold text-sm text-red-900 uppercase tracking-wider">Ausências de frequência (mais de 90 dias)</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                { cat: "consumidor", label: "Consumidor sem nova inscrição", cor: "amber" },
                { cat: "cliente", label: "Cliente sem nova inscrição", cor: "red" },
              ].map((sub) => {
                const items = avisos.ausencia_frequencia.filter((a) => a.categoria === sub.cat);
                return (
                  <div key={sub.cat} className={`rounded-xl border border-${sub.cor}-200 bg-${sub.cor}-50/50 p-3`} data-testid={`aviso-freq-${sub.cat}`}>
                    <div className={`text-[11px] font-bold text-${sub.cor}-900 uppercase tracking-wider mb-2 flex items-center justify-between`}>
                      <span className="truncate">{sub.label}</span>
                      <span className={`px-1.5 py-0.5 rounded-full bg-${sub.cor}-500 text-white text-[10px] tabular-nums`}>{items.length}</span>
                    </div>
                    {items.length === 0 && <div className="text-xs text-slate-400 italic">Todos com frequência recente.</div>}
                    <div className="space-y-1 max-h-48 overflow-auto">
                      {items.map((a, i) => (
                        <button
                          key={i}
                          onClick={() => nav(`/municipio/${a.municipio_id}?cat=${a.categoria}`)}
                          className={`w-full text-left rounded-lg bg-white border border-${sub.cor}-100 hover:border-${sub.cor}-400 hover:shadow-sm transition p-2`}
                          data-testid={`aviso-freq-${sub.cat}-${i}`}
                        >
                          <div className="font-semibold text-sm text-slate-800 flex items-center justify-between gap-2">
                            <span className="truncate">{a.municipio_nome} · {a.uf}</span>
                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full bg-${sub.cor}-500 text-white tabular-nums flex-shrink-0`}>{a.qtd}</span>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5"><b>{a.max_dias} dia{a.max_dias !== 1 ? "s" : ""}</b> sem inscrição</div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid md:grid-cols-3 gap-4">
        <StatBig icon={TrendingUp} label="Ticket Médio Histórico" value={formatBRL(data.ticket_medio_historico)} sub={`Base: ${data.total_inscritos_historico} inscritos · ${formatBRLFull(data.total_receita_historica)} de receita total`} color="emerald" testid="stat-ticket" />
        <StatBig icon={Users} label="Média de Prospecções / dia" value={data.media_prospeccoes_dia.toFixed(1)} sub={`${data.municipios_prospectados_ano} municípios em ${data.dias_ativos} dias ativos`} color="blue" testid="stat-prosp" />
        <StatBig icon={CalendarClock} label="Contatos / dia (média)" value={data.media_contatos_dia.toFixed(1)} sub="Ligação + WhatsApp / dias ativos" color="indigo" testid="stat-cont-dia" />
      </div>

      {/* Metas Diárias da Equipe (Diretoria/Supervisão) */}
      {metaDiaria && metaDiaria.equipe && metaDiaria.equipe.length > 0 && (
        <Card className="p-6 rounded-2xl" data-testid="metas-diarias-equipe">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-amber-600" />
            <h2 className="font-display font-bold text-xl text-slate-900">Meta diária de prospecção de municípios · equipe {metaDiaria.data}</h2>
            <span className="ml-auto text-xs text-slate-400">Município conta com ≥ 4 pessoas efetivas no dia</span>
          </div>
          <div className="space-y-2">
            {metaDiaria.equipe.map((v) => {
              const bateu = v.meta > 0 && v.realizado >= v.meta;
              const pct = Math.min(100, v.percentual);
              return (
                <div
                  key={v.vendedora_id}
                  className={`p-3 rounded-xl border ${v.meta === 0 ? "border-slate-200 bg-slate-50" : bateu ? "border-emerald-300 bg-emerald-50" : "border-amber-200 bg-amber-50/40"}`}
                  data-testid={`meta-diaria-vend-${v.vendedora_id}`}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-semibold text-slate-900 flex items-center gap-2">
                        {v.nome}
                        {v.meta === 0 ? (
                          <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Sem meta</span>
                        ) : bateu ? (
                          <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Bateu! 🎯</span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">{pct.toFixed(0)}%</span>
                        )}
                      </div>
                      {v.meta > 0 ? (
                        <div className="text-xs text-slate-600 mt-0.5">
                          <b className="tabular-nums">{v.realizado}</b> de{" "}
                          <b className="tabular-nums">{v.meta}</b> municípios ·{" "}
                          {bateu ? "meta batida" : `faltam ${Math.max(0, v.meta - v.realizado)}`}
                        </div>
                      ) : (
                        <div className="text-xs text-slate-400 mt-0.5">Ainda não definiu meta diária</div>
                      )}
                    </div>
                    <div className="w-full sm:w-40">
                      <div className="progress-track h-2">
                        <div
                          className={`progress-fill ${bateu ? "win" : pct < 40 ? "warn" : ""}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Top Municípios */}
      <Card className="p-6 rounded-2xl overflow-x-auto" data-testid="top-municipios">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-amber-500" />
          <h2 className="font-display font-bold text-xl text-slate-900">Top 10 Municípios · {data.ano}</h2>
        </div>
        {data.top_municipios.length > 0 ? (
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
                <th className="pb-3 pr-3">#</th>
                <th className="pb-3 pr-3">Município</th>
                <th className="pb-3 pr-3">CNPJ</th>
                <th className="pb-3 pr-3 text-right">Inscritos</th>
                <th className="pb-3 text-right">Receita</th>
              </tr>
            </thead>
            <tbody>
              {data.top_municipios.map((m, i) => (
                <tr key={m.municipio_id}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => nav(`/municipio/${m.municipio_id}`)}
                  data-testid={`top-mun-${m.municipio_id}`}>
                  <td className="py-2 pr-3 font-bold text-slate-400 w-8">{i + 1}</td>
                  <td className="py-2 pr-3 font-semibold">{m.nome} <span className="text-slate-400 text-xs">/ {m.uf}</span></td>
                  <td className="py-2 pr-3 text-slate-500 text-xs">{m.cnpj || "—"}</td>
                  <td className="py-2 pr-3 text-right tabular-nums font-semibold">{m.quantidade}</td>
                  <td className="py-2 text-right tabular-nums">{formatBRL(m.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-slate-400 text-sm py-6 text-center">Nenhuma inscrição registrada no ano ainda.</div>
        )}
      </Card>

      {/* Top Alunos */}
      <Card className="p-6 rounded-2xl overflow-x-auto" data-testid="top-alunos">
        <div className="flex items-center gap-2 mb-4">
          <Award className="w-5 h-5 text-indigo-500" />
          <h2 className="font-display font-bold text-xl text-slate-900">Top 10 Alunos · {data.ano}</h2>
        </div>
        {data.top_alunos.length > 0 ? (
          <table className="w-full text-sm min-w-[500px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
                <th className="pb-3 pr-3">#</th>
                <th className="pb-3 pr-3">Nome</th>
                <th className="pb-3 pr-3">Cargo</th>
                <th className="pb-3 text-right">Inscrições</th>
              </tr>
            </thead>
            <tbody>
              {data.top_alunos.map((a, i) => (
                <tr
                  key={a.pessoa_id || i}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => a.pessoa_id && nav(`/pessoa/${a.pessoa_id}`)}
                  data-testid={`top-aluno-${a.pessoa_id || i}`}
                >
                  <td className="py-2 pr-3 font-bold text-slate-400 w-8">{i + 1}</td>
                  <td className="py-2 pr-3 font-semibold">{a.nome}</td>
                  <td className="py-2 pr-3 text-slate-500 text-xs">{a.funcao || "—"}</td>
                  <td className="py-2 text-right tabular-nums font-semibold">{a.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-slate-400 text-sm py-6 text-center">
            Nenhum aluno cadastrado ainda. Ao registrar uma inscrição, selecione as pessoas do município que farão o curso — elas passam a aparecer aqui.
          </div>
        )}
      </Card>

      {/* Blacklist */}
      <Card className="p-6 rounded-2xl" data-testid="blacklist">
        <div className="flex items-center gap-2 mb-4">
          <Ban className="w-5 h-5 text-red-500" />
          <h2 className="font-display font-bold text-xl text-slate-900">Blacklist — recuperar</h2>
          <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600">{data.blacklist.length}</span>
        </div>
        {data.blacklist.length > 0 ? (
          <div className="space-y-2">
            {data.blacklist.map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3 rounded-xl border border-red-200 bg-red-50/50">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Building2 className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-semibold">{m.nome} <span className="text-slate-400 text-xs">/ {m.uf}</span></div>
                    <div className="text-xs text-red-700 italic truncate">{m.motivo_blacklist || "Sem motivo informado"}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => nav(`/municipio/${m.id}`)} data-testid={`bl-abrir-${m.id}`}>Abrir</Button>
                  <Button size="sm" variant="outline" onClick={() => removerBlacklist(m)} data-testid={`bl-remove-${m.id}`} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50">Recuperar</Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-slate-400 text-sm py-6 text-center">
            Nenhum município na blacklist. Adicione manualmente pela página do município se precisar recuperar clientes na concorrência.
          </div>
        )}
      </Card>
    </div>
  );
}
